'use client';

import { Modal } from '@/components/layouts/Modal';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ModalInstance extends Props {
  id: string;
  isOpen: boolean;
}

interface Props {
  component: (props: { onClose: () => void }) => React.ReactNode;
  onClose?: () => void;
  onShow?: () => void;
  disableBackdropClick?: boolean;
  backdropClassName?: string;
  viewType?: 'full' | 'top' | 'bottom' | 'center';
}

type ContextStateType = {
  handleModalOpen: (data: Props) => void;
  handleModalClose: () => void;
  handleModalUpdate: (updates: Partial<Props>) => void;
  isModalOpen: boolean;
};

const ModalContext = createContext<ContextStateType | null>(null);

const ModalContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<ModalInstance[]>([]);

  const isModalOpen = useMemo(() => modals.some((m) => m.isOpen), [modals]);

  const handleModalOpen = useCallback((props: Props) => {
    const id = Math.random().toString(36).substring(2, 9);
    setModals((prev) => [...prev, { ...props, id, isOpen: true }]);
    return id;
  }, []);

  const handleModalUpdate = useCallback((updates: Partial<Props>) => {
    setModals((prev) => {
      const lastOpenIndex = prev.map((m) => m.isOpen).lastIndexOf(true);
      if (lastOpenIndex === -1) return prev;

      const newModals = [...prev];
      newModals[lastOpenIndex] = { ...newModals[lastOpenIndex], ...updates };
      return newModals;
    });
  }, []);

  const closeModalById = useCallback((id: string) => {
    setModals((prev) => prev.map((m) => (m.id === id ? { ...m, isOpen: false } : m)));
    setTimeout(() => {
      setModals((prev) => prev.filter((m) => m.id !== id));
    }, 400);
  }, []);

  const handleModalClose = useCallback(() => {
    const lastModal = [...modals].reverse().find((m) => m.isOpen);
    if (lastModal) closeModalById(lastModal.id);
  }, [modals, closeModalById]);

  return (
    <ModalContext.Provider value={{ handleModalOpen, handleModalClose, handleModalUpdate, isModalOpen }}>
      {children}
      {modals.map((modal) => (
        <Modal
          key={modal.id}
          isOpen={modal.isOpen}
          onClose={() => closeModalById(modal.id)}
          disableBackdropClick={modal.disableBackdropClick}
          viewType={modal.viewType}
          backdropClassName={modal.backdropClassName}
        >
          {modal.component({ onClose: () => closeModalById(modal.id) })}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
};

const useModalContext = (): ContextStateType => {
  const modalContext = useContext(ModalContext);

  if (!modalContext) {
    throw Error('useModalContext hook should be wrapped by ModalContextProvider');
  }

  return modalContext;
};

export { ModalContextProvider, useModalContext };
